// useAuth Hook
// This hook provides a React interface to Firebase Authentication services.
// It manages authentication state and provides methods for login, register, logout, etc.

import { createContext, createElement, useContext, useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { authService } from '../firebase';
import { getFriendlyAuthError } from '../lib/firebaseErrors';
import { store } from '../store';
import { resetFilters } from '../store/slices/filtersSlice';
import { resetBillingEntitlement } from '../store/slices/billingSlice';
import { resetFavorites } from '../store/slices/favoritesSlice';
import { resetScreening } from '../store/slices/screeningSlice';

const LOGOUT_FLAG_KEY = 'auth_logged_out';

export interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
}

// Authentication state interface
export interface AuthState {
  user: UserProfile | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isFirebaseAvailable: boolean;
  roles: string[];
  isAdmin: boolean;
}

// Registration result interface
export interface RegisterResult {
  user: UserProfile;
  verificationSent: boolean;
}

// Authentication methods interface
export interface AuthMethods {
  login: (email: string, password: string) => Promise<UserProfile>;
  register: (email: string, password: string) => Promise<RegisterResult>;
  loginWithGoogle: () => Promise<UserProfile>;
  registerWithGoogle: () => Promise<UserProfile>;
  logout: () => Promise<void>;
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>;
  getRoles: () => Promise<string[]>;
  sendVerificationEmail: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  clearError: () => void;
  refreshAuthState: () => void;
  /** When Firebase is not available, re-enter app as dev user (clears logout state). */
  devBypassLogin: () => void;
}

// Combined hook return type
export interface UseAuthReturn extends AuthState, AuthMethods {}

const DEV_USER: UserProfile = {
  uid: 'dev-admin',
  // D.10: placeholder address only. DEV_USER is the non-production dev-bypass
  // identity; it must not embed a real operator email in the public bundle.
  email: 'dev-admin@localhost',
  displayName: 'Dev Admin',
  photoURL: null,
  emailVerified: true,
};

// Hook implementation
const useProvideAuth = (): UseAuthReturn => {
  const isProductionBuild = process.env.NODE_ENV === 'production';
  const canUseDevBypass = !isProductionBuild;
  const initialExplicitLogout =
    typeof sessionStorage !== 'undefined' && sessionStorage.getItem(LOGOUT_FLAG_KEY) === '1';
  const bypassAuthOnInit =
    canUseDevBypass && (process.env.REACT_APP_SELF_HOSTED === 'true' || !authService.isAvailable());

  const [user, setUser] = useState<UserProfile | null>(() =>
    bypassAuthOnInit && !initialExplicitLogout ? DEV_USER : null
  );
  const [loading, setLoading] = useState<boolean>(() => !bypassAuthOnInit);
  const [error, setError] = useState<string | null>(null);
  const [roles, setRoles] = useState<string[]>(() =>
    bypassAuthOnInit && !initialExplicitLogout ? ['admin'] : []
  );
  // When user explicitly logs out, we set this (and sessionStorage); respect it on load so they stay logged out.
  const [explicitLogout, setExplicitLogout] = useState<boolean>(initialExplicitLogout);
  // D.6/D.10: admin determination is sourced from the server (GET /api/me/roles)
  // instead of a hardcoded email/uid allowlist baked into the JS bundle.
  const [serverIsAdmin, setServerIsAdmin] = useState<boolean>(false);

  const explicitLogoutRef = useRef(explicitLogout);
  explicitLogoutRef.current = explicitLogout;

  const strictGoogleLoginInProgressRef = useRef(false);

  // Check if Firebase is available
  const isFirebaseAvailable = useMemo(() => {
    const available = authService.isAvailable();
    // Dev-only debug log - too noisy for prod consoles, and the credential-
    // presence booleans are mildly information-leaky.
    if (process.env.NODE_ENV !== 'production') {
      console.log('[Auth] Firebase available:', available, {
        apiKey: !!process.env.REACT_APP_FIREBASE_API_KEY,
        projectId: !!process.env.REACT_APP_FIREBASE_PROJECT_ID
      });
    }
    return available;
  }, []);

  // Derived state
  const isAuthenticated = useMemo(() => {
    const result = (() => {
      if (explicitLogout) return false;
      // Bypass auth only for non-production self-hosted deployments.
      if (canUseDevBypass && process.env.REACT_APP_SELF_HOSTED === 'true') {
        return true;
      }
      return user !== null;
    })();
    if (process.env.NODE_ENV !== 'production') {
      console.log('[Auth] isAuthenticated computed:', result, { explicitLogout, user: user?.email, selfHosted: process.env.REACT_APP_SELF_HOSTED });
    }
    return result;
  }, [user, explicitLogout, canUseDevBypass]);

  const isAdmin = useMemo(() => {
    // Dev-bypass admin only for the synthetic dev-admin identity - not every
    // real Firebase login while REACT_APP_SELF_HOSTED=true (local npm start).
    if (
      canUseDevBypass &&
      user?.uid === DEV_USER.uid &&
      (process.env.REACT_APP_SELF_HOSTED === 'true' || !isFirebaseAvailable)
    ) {
      return true;
    }
    // Check if user has admin role from Firebase claims
    const hasAdminRole = roles.some((r) => String(r).toLowerCase() === 'admin');
    if (hasAdminRole) return true;

    // D.6/D.10: operator allowlist moved SERVER-SIDE. `serverIsAdmin` is set
    // from GET /api/me/roles, which derives admin from the server ADMIN_EMAILS
    // env (and grants admin in single-operator auth-off mode), so the operator
    // email + Firebase UID no longer ship in the public JS bundle.
    if (serverIsAdmin) return true;

    // PR-B2 (spec §5.3): REACT_APP_ADMIN_EMAILS is BREAK-GLASS only.
    // Backend mirrors this with ADMIN_BREAK_GLASS=true; without parity an
    // operator could grant themselves admin in the UI but be rejected by
    // the backend (or vice versa) - confusing failure mode.
    const breakGlassRaw = (process.env.REACT_APP_ADMIN_BREAK_GLASS || '').trim().toLowerCase();
    const breakGlassOn = breakGlassRaw === '1' || breakGlassRaw === 'true' || breakGlassRaw === 'yes' || breakGlassRaw === 'on';
    const adminEmails = process.env.REACT_APP_ADMIN_EMAILS || '';
    if (breakGlassOn && adminEmails && user?.email) {
      const adminEmailList = adminEmails.split(',').map(e => e.trim().toLowerCase());
      if (adminEmailList.includes(user.email.toLowerCase())) {
        return true;
      }
    }

    return false;
  }, [roles, canUseDevBypass, user, serverIsAdmin, isFirebaseAvailable]);

  // D.6/D.10: fetch the server's admin determination so the operator allowlist
  // is not hardcoded in the bundle. Best-effort; on failure the admin tab just
  // stays hidden (dev-bypass and the Firebase 'admin' claim path still apply).
  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated) {
      setServerIsAdmin(false);
      return;
    }
    (async () => {
      try {
        const token = await authService.getIdToken();
        const resp = await fetch('/api/me/roles', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled && resp.ok) {
          const data = await resp.json();
          setServerIsAdmin(Boolean(data?.is_admin));
        }
      } catch {
        /* keep prior value; admin UI stays hidden on failure */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user]);

  // Clear error helper
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Set up auth state listener
  useEffect(() => {
    const sessionStorageFlag = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(LOGOUT_FLAG_KEY) : 'undefined';
    console.log('[Auth] useEffect running, isFirebaseAvailable:', isFirebaseAvailable, 'SELF_HOSTED:', process.env.REACT_APP_SELF_HOSTED, 'sessionStorageFlag:', sessionStorageFlag);

    // Handle SELF_HOSTED mode first
    if (canUseDevBypass && process.env.REACT_APP_SELF_HOSTED === 'true') {
      console.log('[Auth] SELF_HOSTED mode, checking sessionStorage flag');
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(LOGOUT_FLAG_KEY) === '1') {
        // Explicitly logged out in SELF_HOSTED mode: user is logged out, but we still need
        // to set up Firebase listener so login with Google can work!
        console.log('[Auth] SELF_HOSTED + explicit logout - setting user to null but still setting up Firebase listener');
        setUser(null);
        setRoles([]);
        setLoading(false);
        // Don't return early! Fall through to set up Firebase listener for Google login
      } else {
        // Not explicitly logged out in SELF_HOSTED mode - auto-login as dev user
        console.log('[Auth] SELF_HOSTED - setting dev user');
        setLoading(false);
        setUser(DEV_USER);
        setRoles(['admin']);
        return; // No Firebase listener needed when using dev user
      }
    }

    // Handle Firebase unavailability
    if (!isFirebaseAvailable) {
      // In production, never auto-bypass authentication when Firebase is unavailable.
      if (!canUseDevBypass) {
        console.error('[Auth] Firebase unavailable in production - refusing dev bypass');
        setError('Authentication service is currently unavailable. Please try again later.');
        setUser(null);
        setRoles([]);
        setLoading(false);
        return;
      }

      // Non-production fallback: set dev user only if user has NOT explicitly logged out.
      console.log('[Auth] Firebase not available, checking sessionStorage flag, explicitLogout state:', explicitLogout);
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(LOGOUT_FLAG_KEY) === '1') {
        console.log('[Auth] Firebase unavailable + explicit logout flag set - setting user to null');
        setUser(null);
        setRoles([]);
        setLoading(false);
        return;
      }
      // ALSO check explicitLogout state directly as backup
      if (explicitLogout) {
        console.log('[Auth] Firebase unavailable + explicitLogout state is true - keeping user logged out');
        setUser(null);
        setRoles([]);
        setLoading(false);
        return;
      }
      console.log('[Auth] Firebase unavailable - setting dev user (no logout flag, explicitLogout:', explicitLogout, ')');
      setLoading(false);
      setUser(DEV_USER);
      setRoles(['admin']);
      return;
    }

    // Firebase is available - always set up listener so login with Google can work
    console.log('[Auth] Setting up auth state listener, explicitLogoutRef.current:', explicitLogoutRef.current);

    const unsubscribe = authService.onAuthStateChanged(async (authUser: UserProfile | null) => {
      if (strictGoogleLoginInProgressRef.current) {
        // Strict Google sign-in owns user state during its lifecycle.
        return;
      }
      // PII (email) - dev only.
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Auth] State changed:', authUser ? `User: ${authUser.email}` : 'No user', '| explicitLogoutRef:', explicitLogoutRef.current);
      }
      // If user explicitly logged out, do not restore from Firebase persistence - keep them logged out.
      if (authUser && explicitLogoutRef.current) {
        console.log('[Auth] User came back but explicitLogout is true - blocking restore');
        setUser(null);
        setRoles([]);
        setLoading(false);
        return;
      }
      if (authUser) {
        if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(LOGOUT_FLAG_KEY);
        setExplicitLogout(false);
      }
      setUser(authUser);
      // Unblock the app as soon as identity is known - do NOT hold AuthGate's
      // "Checking authentication..." gate for the getRoles() round-trip, which
      // caused a 3-7s flash on full-reload (?tab=) deep links. Roles load in
      // the background; admin gating tolerates a brief empty-roles window and
      // the server still enforces admin authorization.
      setLoading(false);
      if (authUser) {
        setError(null);
        authService
          .getRoles()
          .then((r) => setRoles(Array.isArray(r) ? r : []))
          .catch(() => setRoles([]));
      } else {
        setRoles([]);
      }
    });

    // Cleanup function
    return () => {
      if (unsubscribe) {
        console.log('[Auth] Cleaning up auth state listener');
        unsubscribe();
      }
    };
  }, [isFirebaseAvailable, explicitLogout, canUseDevBypass]);

  // Manual auth state refresh (useful for debugging or force refresh)
  const refreshAuthState = useCallback(() => {
    if (!isFirebaseAvailable) {
      return;
    }

    console.log('[Auth] Manually refreshing auth state');
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
    // Refresh roles as well
    authService.getRoles().then(r => setRoles(Array.isArray(r) ? r : [])).catch(() => setRoles([]));
  }, [isFirebaseAvailable]);

  // Login with email and password
  const login = useCallback(async (email: string, password: string): Promise<UserProfile> => {
    if (!isFirebaseAvailable) {
      throw new Error('Firebase Auth is not available');
    }

    try {
      setLoading(true);
      setError(null);
      // PII (email) - dev only.
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Auth] Attempting login for:', email);
      }

      const userProfile = await authService.signInWithEmail(email, password);
      if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(LOGOUT_FLAG_KEY);
      setExplicitLogout(false);
      // PII (email) - dev only.
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Auth] Login successful:', userProfile.email);
      }
      return userProfile;
    } catch (err: any) {
      console.error('[Auth] Login failed:', err);
      // Friendly text BEFORE setError so the global `error` state never
      // briefly holds the raw "Firebase: Error (auth/invalid-credential)."
      // string between this setError and LoginModal's catch setting its
      // own localError. The modal renders `localError || error` - making
      // both layers friendly removes the perceptible flash.
      // Static fallback - passing err.message would leak raw Firebase
      // SDK text for codes not in the map. The full err is logged above.
      setError(getFriendlyAuthError(err?.code, 'Sign in failed. Please try again.'));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isFirebaseAvailable]);

  // Register with email and password
  const register = useCallback(async (email: string, password: string): Promise<RegisterResult> => {
    if (!isFirebaseAvailable) {
      throw new Error('Firebase Auth is not available');
    }

    try {
      setLoading(true);
      setError(null);
      // PII (email) - dev only.
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Auth] Attempting registration for:', email);
      }

      // Registration auto-signs-in the (unverified) user, which fires
      // onAuthStateChanged - possibly DURING the await below. The listener
      // reads explicitLogoutRef synchronously and suppresses the user when the
      // gate is still set (a stale flag from an earlier sign-out this session),
      // so isAuthenticated never flips and AuthGate's EmailVerificationPage
      // never shows. Clear the gate BEFORE the call, and write the ref
      // imperatively - setExplicitLogout(false) alone only reaches the ref on
      // the next render (line 90), too late for a listener that fires mid-await.
      explicitLogoutRef.current = false;
      if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(LOGOUT_FLAG_KEY);
      setExplicitLogout(false);

      const result = await authService.registerWithEmail(email, password);
      // Defensive: if the listener fired-and-blocked before the ref write
      // above (e.g. a synchronous auth event), recover the user here so the
      // verification gate still renders. Mirrors loginWithGoogle's own-state.
      setUser(result.user);
      // PII (email) - dev only.
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Auth] Registration successful:', result.user.email, '| Verification sent:', result.verificationSent);
      }
      return result;
    } catch (err: any) {
      console.error('[Auth] Registration failed:', err);
      setError(getFriendlyAuthError(err?.code, 'Registration failed. Please try again.'));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isFirebaseAvailable]);

  // Login with Google
  const loginWithGoogle = useCallback(async (): Promise<UserProfile> => {
    if (!isFirebaseAvailable) {
      throw new Error('Firebase Auth is not available');
    }
    strictGoogleLoginInProgressRef.current = true;
    try {
      setLoading(true);
      setError(null);
      console.log('[Auth] Attempting Google login');
      const userProfile = await authService.signInWithGoogle();
      if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(LOGOUT_FLAG_KEY);
      setExplicitLogout(false);
      setUser(userProfile);
      try {
        const r = await authService.getRoles();
        setRoles(Array.isArray(r) ? r : []);
      } catch {
        setRoles([]);
      }
      // PII (email) - dev only.
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Auth] Google login successful:', userProfile.email);
      }
      return userProfile;
    } catch (err: any) {
      if (err?.code === 'auth/new-user-must-register') {
        setUser(null);
        setRoles([]);
        setExplicitLogout(true);
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(LOGOUT_FLAG_KEY, '1');
        }
      }
      console.error('[Auth] Google login failed:', err);
      // Don't pollute the global error banner when the user simply
      // cancelled the popup - LoginModal also intentionally suppresses
      // these codes from its localError. Other codes still flow through
      // the friendly mapper.
      if (
        err?.code !== 'auth/popup-closed-by-user' &&
        err?.code !== 'auth/cancelled-popup-request'
      ) {
        setError(getFriendlyAuthError(err?.code, 'Google sign in failed. Please try again.'));
      }
      throw err;
    } finally {
      strictGoogleLoginInProgressRef.current = false;
      setLoading(false);
    }
  }, [isFirebaseAvailable]);

  const registerWithGoogle = useCallback(async (): Promise<UserProfile> => {
    if (!isFirebaseAvailable) {
      throw new Error('Firebase Auth is not available');
    }
    try {
      setLoading(true);
      setError(null);
      console.log('[Auth] Attempting Google registration');
      const userProfile = await authService.registerWithGoogle();
      if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(LOGOUT_FLAG_KEY);
      setExplicitLogout(false);
      // PII (email) - dev only.
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Auth] Google registration successful:', userProfile.email);
      }
      return userProfile;
    } catch (err: any) {
      console.error('[Auth] Google registration failed:', err);
      if (
        err?.code !== 'auth/popup-closed-by-user' &&
        err?.code !== 'auth/cancelled-popup-request'
      ) {
        setError(getFriendlyAuthError(err?.code, 'Google sign up failed. Please try again.'));
      }
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isFirebaseAvailable]);

  // Logout: clear credentials and redirect to login page (AuthGate shows LoginPage when !isAuthenticated).
  const logout = useCallback(async (): Promise<void> => {
    console.log('[Auth] Logout called, explicitLogout currently:', explicitLogoutRef.current);
    // 1. Persist "logged out" so refresh still shows login (and we ignore Firebase persistence restore).
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(LOGOUT_FLAG_KEY, '1');
      // Reset auto-scan flag so next login re-triggers the on-login screening.
      sessionStorage.removeItem('auto_scan_done_this_session');
    }
    setExplicitLogout(true);
    console.log('[Auth] Set explicitLogout=true, sessionStorage flag set');
    setUser(null);
    setRoles([]);
    setError(null);
    // Clear per-user filter config from the Redux store so the next user who
    // logs in on this client does NOT inherit this user's filters. Use the store
    // singleton (not useAppDispatch): AuthProvider is mounted standalone in tests
    // without a redux <Provider>, where the hook form would throw.
    store.dispatch(resetFilters());
    store.dispatch(resetBillingEntitlement());
    // Clear the prior user's favorites + screening results too — the auth-change
    // effect no longer re-fetches on logout (it now gates on isAuthenticated), so
    // without this the previous user's data would linger until a hard refresh.
    store.dispatch(resetFavorites());
    store.dispatch(resetScreening());

    if (!isFirebaseAvailable) {
      console.log('[Auth] Firebase not available, returning early from logout');
      return;
    }

    try {
      setLoading(true);
      console.log('[Auth] Attempting Firebase signOut');

      await authService.signOut();
      console.log('[Auth] Firebase signOut completed');
    } catch (err: any) {
      console.error('[Auth] Logout failed:', err);
      setError(getFriendlyAuthError(err?.code, 'Sign out failed. Please try again.'));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isFirebaseAvailable]);

  /** When Firebase is not available, re-enter the app as dev user (clears logout state). */
  const devBypassLogin = useCallback(() => {
    if (!canUseDevBypass) {
      setError('Development bypass is disabled in production.');
      return;
    }
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(LOGOUT_FLAG_KEY);
    setExplicitLogout(false);
    setUser(DEV_USER);
    setRoles(['admin']);
    setError(null);
  }, [canUseDevBypass]);

  // Roles from custom claims
  const getRoles = useCallback(async (): Promise<string[]> => {
    try {
      return await authService.getRoles();
    } catch {
      return [];
    }
  }, []);

  // Get ID token
  const getIdToken = useCallback(async (forceRefresh = false): Promise<string | null> => {
    if (!isFirebaseAvailable || !user) {
      return null;
    }

    try {
      const token = await authService.getIdToken(forceRefresh);
      if (token) {
        console.log('[Auth] Retrieved ID token');
      }
      return token;
    } catch (err: any) {
      console.error('[Auth] Failed to get ID token:', err);
      return null;
    }
  }, [isFirebaseAvailable, user]);

  // Send email verification (resend) for current user
  const sendVerificationEmail = useCallback(async (): Promise<void> => {
    if (!isFirebaseAvailable) {
      throw new Error('Firebase Auth is not available');
    }
    await authService.sendVerificationEmail();
  }, [isFirebaseAvailable]);

  // Send password reset email
  const sendPasswordReset = useCallback(async (email: string): Promise<void> => {
    if (!isFirebaseAvailable) {
      throw new Error('Firebase Auth is not available');
    }
    await authService.sendPasswordReset(email);
  }, [isFirebaseAvailable]);

  // Return hook interface
  return {
    // State
    user,
    loading,
    error,
    isAuthenticated,
    isFirebaseAvailable,
    roles,
    isAdmin,

    // Methods
    login,
    register,
    loginWithGoogle,
    registerWithGoogle,
    logout,
    getIdToken,
    getRoles,
    sendVerificationEmail,
    sendPasswordReset,
    clearError,
    refreshAuthState,
    devBypassLogin,
  };
};

const AuthContext = createContext<UseAuthReturn | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const auth = useProvideAuth();
  return createElement(AuthContext.Provider, { value: auth }, children);
};

export const useAuth = (): UseAuthReturn => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Export default
export default useAuth;